import debounce from 'lodash/debounce';
import {
  rejectOnCancel,
  CancelToken,
} from 'app/utils/cancelToken';

/**
 * Takes a function and returns a new function that will be called once during the next animation
 * frame (via `requestAnimationFrame`) no matter how many times you call it before the next frame.
 *
 * The returned function will also have a `.cancel` method to stop the function from being called
 * during the next frame, similar to functions returned by lodash's `throttle` and `debounce`.
 */
export const animationFrameDebounce = <Args extends any[]>(func: (...args: Args) => void) =>
  baseDebounce(func, requestAnimationFrame, cancelAnimationFrame);

/**
 * Like animationFrameDebounce, but using requestIdleCallback() instead of requestAnimationFrame()
 */
export const idleCallbackDebouce = <Args extends any[]>(func: (...args: Args) => void) =>
  baseDebounce(func, requestIdleCallback, cancelIdleCallback);

function baseDebounce<Args extends any[], IdType>(
  func: (...args: Args) => void,
  requestFunction: (f: () => void) => IdType,
  cancelFunction: (id: IdType) => void,
): {
  (...args: Args): void,
  cancel(): void,
} {
  let nextFrameId: IdType | undefined;

  const cancel = () => {
    if (nextFrameId !== undefined) {
      cancelFunction(nextFrameId);
      nextFrameId = undefined;
    }
  };

  const debouncedFunc = (...args: Args) => {
    if (!nextFrameId) {
      nextFrameId = requestFunction(() => {
        nextFrameId = undefined;

        func(...args);
      });
    }
  };

  debouncedFunc.cancel = cancel;

  return debouncedFunc;
}

export const sleep = async (delay: number, cancelToken?: CancelToken): Promise<void> => {
  const { promise, resolve } = createExternallyControlledPromise();

  const timeoutId = setTimeout(() => resolve(), delay);

  try {
    await Promise.race([
      promise,
      rejectOnCancel(cancelToken),
    ]);
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Wraps a function that takes the last argument as a `(error, result)`
 * callback and makes it return a Promise that rejects if error is
 * truthy. Otherwise it resolves to `result`.
 */
export const callbackAsPromise: {
  // Add type variants for 0-2 arguments to the function before the callback
  <Result, Error, A1, A2>(
    fn: (
      a1: A1,
      a2: A2,
      callback: (error: Error | null, result: Result) => void
    ) => void,
    a1: A1,
    a2: A2
  ): Result;
  <Result, Error, A1>(
    fn: (
      a1: A1,
      callback: (error: Error | null, result: Result) => void
    ) => void,
    a1: A1
  ): Result;
  <Result, Error>(
    fn: (
      callback: (error: Error | null, result: Result) => void
    ) => void
  ): Result;
} = (fn: (...args: any[]) => void, ...args: unknown[]) => new Promise<unknown>((resolve, reject) => {
  try {
    // It's impossible to typecheck `...` arguments in TS if they're
    // not at the end of the list so we instead guarantee type
    // safety for the caller but ignore it in the implementation
    fn(...args, (error: unknown, result: unknown) => {
      if (error) {
        reject(error);
      } else {
        resolve(result);
      }
    });
  } catch (error) {
    reject(error);
  }
});

interface IMinimalEmitter<EventName, EventType> {
  addEventListener(name: EventName, listener: (event: EventType) => void): void;
  removeEventListener(name: EventName, listener: (event: EventType) => void): void;
}

/**
 * Waits `delay` time for an event and resolves to the last occurence.
 */
export const untilLastEvent = <
  EventName,
  EventType,
>(
  emitter: IMinimalEmitter<EventName, EventType>,
  eventName: EventName,
  delay: number,
): Promise<EventType> => new Promise((resolve) => {
  const handler = debounce((event: EventType) => {
    emitter.removeEventListener(eventName, handler);
    resolve(event);
  }, delay);

  emitter.addEventListener(eventName, handler);
});

export const until = <
  EventName,
  EventType,
>(
  emitter: IMinimalEmitter<EventName, EventType>,
  eventName: EventName,
): Promise<EventType> => new Promise((resolve) => {
  const handler = (event: EventType) => {
    emitter.removeEventListener(eventName, handler);
    resolve(event);
  };

  emitter.addEventListener(eventName, handler);
});

export interface IExternallyControlledPromise<T> {
  promise: Promise<T>;
  resolve: <V extends T>(value?: V) => void;
  reject: (reason?: any) => void;
}

/**
 * Creates a promise and exposes the resolve and reject function allowing it be controlled externally.
 */
export const createExternallyControlledPromise = <T>(): IExternallyControlledPromise<T> => {
  let resolveRef: IExternallyControlledPromise<T>['resolve'];
  let rejectRef: IExternallyControlledPromise<T>['reject'];

  const promise = new Promise<T>((resolve, reject) => {
    resolveRef = resolve;
    rejectRef = reject;
  });

  return {
    promise,
    reject: rejectRef!,
    resolve: resolveRef!,
  };
};
